import React from 'react';
import { Bell, Search, Filter, RefreshCw, Shield, AlertTriangle, Check, ExternalLink, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { useLoading } from '../../hooks/useLoading';
import { sleep } from '../../utils/sleep';

const SecurityAlerts = () => {
  const { isLoading, withLoading } = useLoading();

  const handleRefresh = async () => {
    await withLoading(async () => {
      await sleep(3000);
      console.log('Refreshing security alerts...');
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Refreshing alerts..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Bell className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Security Alerts</h1>
            <p className="text-sm text-gray-400">Monitor and manage security notifications</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button>
            <Shield className="h-4 w-4 mr-2" />
            Configure Alerts
          </Button>
        </div>
      </div>

      {/* Alert Stats */}
      <div className="grid grid-cols-4 gap-6">
        {[
          { label: 'Active Alerts', value: '24', trend: '+5', icon: Bell, color: 'text-blue-500' },
          { label: 'Critical', value: '3', trend: '+1', icon: AlertTriangle, color: 'text-red-500' },
          { label: 'Resolved', value: '156', trend: '+12', icon: Check, color: 'text-emerald-500' },
          { label: 'Alert Rules', value: '18', trend: '+2', icon: Shield, color: 'text-purple-500' }
        ].map((stat) => (
          <div key={stat.label} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">{stat.label}</span>
              <stat.icon className={`h-5 w-5 ${stat.color}`} />
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className={`text-xs ${stat.trend.startsWith('+') ? 'text-red-500' : 'text-emerald-500'}`}>
                {stat.trend}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Active Alerts */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Active Alerts</h2>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
              <input
                type="text"
                placeholder="Search alerts..."
                className="pl-9 pr-4 py-2 bg-gray-800/30 border border-gray-800 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {[
            {
              title: 'Critical Vulnerability Detected',
              description: 'SQL injection vulnerability found in login endpoint',
              type: 'security',
              severity: 'critical',
              time: '10m ago',
              asset: 'api.example.com'
            },
            {
              title: 'Suspicious Login Activity',
              description: 'Multiple failed login attempts detected',
              type: 'authentication',
              severity: 'high',
              time: '30m ago',
              asset: 'admin.example.com'
            },
            {
              title: 'SSL Certificate Expiring',
              description: 'SSL certificate will expire in 7 days',
              type: 'system',
              severity: 'medium',
              time: '1h ago',
              asset: 'example.com'
            }
          ].map((alert, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg group">
              <div className="flex items-center space-x-4">
                <div className={`p-2 rounded-lg ${
                  alert.severity === 'critical' ? 'bg-red-500/10' :
                  alert.severity === 'high' ? 'bg-orange-500/10' :
                  'bg-yellow-500/10'
                }`}>
                  <AlertTriangle className={`h-5 w-5 ${
                    alert.severity === 'critical' ? 'text-red-500' :
                    alert.severity === 'high' ? 'text-orange-500' :
                    'text-yellow-500'
                  }`} />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-white">{alert.title}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      alert.severity === 'critical' ? 'bg-red-500/10 text-red-500' :
                      alert.severity === 'high' ? 'bg-orange-500/10 text-orange-500' :
                      'bg-yellow-500/10 text-yellow-500'
                    }`}>
                      {alert.severity}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400 mt-1">{alert.description}</div>
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-xs text-gray-500">Asset: {alert.asset}</span>
                    <span className="text-xs text-gray-500">{alert.time}</span>
                    <span className="text-xs text-gray-500">Type: {alert.type}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="outline" size="sm">
                  <Check className="h-4 w-4 mr-2" />
                  Resolve
                </Button>
                <Button variant="outline" size="sm">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Investigate
                </Button>
              </div>
            </div>
          ))}
         Continuing directly with the SecurityAlerts.tsx file content from where we left off:

```tsx
        </div>
      </div>

      {/* Alert Categories and Response Times */}
      <div className="grid grid-cols-2 gap-6">
        {/* Alert Categories */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Alert Categories</h2>
          <div className="space-y-4">
            {[
              { name: 'Security Vulnerabilities', count: 45, percentage: 35 },
              { name: 'Authentication Issues', count: 32, percentage: 25 },
              { name: 'System Alerts', count: 28, percentage: 20 },
              { name: 'Configuration Issues', count: 24, percentage: 15 }
            ].map((category) => (
              <div key={category.name} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-300">{category.name}</span>
                  <span className="text-white">{category.count}</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${category.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Response Times */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Response Times</h2>
          <div className="space-y-4">
            {[
              { severity: 'Critical', time: '< 15 min', count: 12, status: 'on-target' },
              { severity: 'High', time: '< 1 hour', count: 24, status: 'warning' },
              { severity: 'Medium', time: '< 4 hours', count: 36, status: 'on-target' },
              { severity: 'Low', time: '< 24 hours', count: 48, status: 'on-target' }
            ].map((item) => (
              <div key={item.severity} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-white">{item.severity} Alerts</div>
                  <div className="text-xs text-gray-400 mt-1">Target: {item.time}</div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className="text-sm font-medium text-white">{item.count}</div>
                    <div className="text-xs text-gray-400">alerts</div>
                  </div>
                  <div className={`h-2 w-2 rounded-full ${
                    item.status === 'on-target' ? 'bg-emerald-500' : 'bg-yellow-500'
                  }`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alert Rules */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Alert Rules</h2>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Rule
          </Button>
        </div>

        <div className="space-y-4">
          {[
            {
              name: 'Critical Vulnerabilities',
              description: 'Alert on any critical security vulnerability',
              type: 'security',
              status: 'active',
              triggers: 24
            },
            {
              name: 'Failed Authentication',
              description: 'Multiple failed login attempts within 5 minutes',
              type: 'authentication',
              status: 'active',
              triggers: 156
            },
            {
              name: 'SSL Certificate Monitor',
              description: 'Alert when SSL certificates are near expiration',
              type: 'system',
              status: 'active',
              triggers: 12
            }
          ].map((rule, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg group">
              <div className="flex items-center space-x-4">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Shield className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">{rule.name}</div>
                  <div className="text-sm text-gray-400 mt-1">{rule.description}</div>
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-xs text-gray-500">Type: {rule.type}</span>
                    <span className="text-xs text-gray-500">{rule.triggers} triggers</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="outline" size="sm">Edit</Button>
                <Button variant="outline" size="sm">Disable</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SecurityAlerts;